/**
 * Arithmétique de dates civiles, en AAAA-MM-JJ.
 *
 * Un budget raisonne en jours, pas en instants : aucun fuseau, aucune heure.
 * Tout passe par des dates UTC en interne, ce qui évite qu'un changement
 * d'heure fasse gagner ou perdre un jour au milieu d'un cycle.
 */

export type ISODate = string;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export interface CivilDate {
  year: number;
  /** 1 à 12 — pas l'index 0-11 de `Date`, source d'erreurs à répétition. */
  month: number;
  day: number;
}

export function parseDate(iso: ISODate): CivilDate {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match === null) throw new Error(`Date invalide : « ${iso} » (attendu AAAA-MM-JJ)`);
  const [, year = '', month = '', day = ''] = match;
  return { year: Number(year), month: Number(month), day: Number(day) };
}

export function toISO(date: CivilDate): ISODate {
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');
  return `${date.year}-${month}-${day}`;
}

function toEpochDay(iso: ISODate): number {
  const { year, month, day } = parseDate(iso);
  return Date.UTC(year, month - 1, day) / MS_PER_DAY;
}

function fromEpochDay(epochDay: number): ISODate {
  const date = new Date(epochDay * MS_PER_DAY);
  return toISO({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
}

/** Nombre de jours de `from` à `to`. Négatif si `to` précède `from`. */
export function daysBetween(from: ISODate, to: ISODate): number {
  return toEpochDay(to) - toEpochDay(from);
}

export function addDays(iso: ISODate, days: number): ISODate {
  return fromEpochDay(toEpochDay(iso) + days);
}

export function lastDayOfMonth(year: number, month: number): number {
  // Le jour 0 du mois suivant est le dernier jour du mois courant.
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Ramène un jour au dernier du mois quand il n'existe pas.
 * Un prélèvement au 31 tombe le 30 en avril, le 28 en février.
 */
export function clampDayToMonth(year: number, month: number, day: number): number {
  return Math.min(day, lastDayOfMonth(year, month));
}

/** Décale de N mois en conservant le jour, ramené au dernier du mois si besoin. */
export function addMonths(iso: ISODate, months: number): ISODate {
  const { year, month, day } = parseDate(iso);
  const total = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(total / 12);
  // Pas `total % 12` : le reste de JavaScript est négatif pour un total négatif.
  const targetMonth = total - targetYear * 12 + 1;
  return toISO({
    year: targetYear,
    month: targetMonth,
    day: clampDayToMonth(targetYear, targetMonth, day),
  });
}

/** Nombre de mois entiers entre deux dates, jour anniversaire compris. */
export function monthsBetween(from: ISODate, to: ISODate): number {
  const a = parseDate(from);
  const b = parseDate(to);
  let months = (b.year - a.year) * 12 + (b.month - a.month);
  // Le jour anniversaire n'est pas encore atteint : le mois n'est pas révolu.
  if (b.day < a.day) months -= 1;
  return months;
}

export function compareDates(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function minDate(a: ISODate, b: ISODate): ISODate {
  return a <= b ? a : b;
}

export function maxDate(a: ISODate, b: ISODate): ISODate {
  return a >= b ? a : b;
}

export function isWithin(date: ISODate, from: ISODate, to: ISODate): boolean {
  return date >= from && date <= to;
}

/** Toutes les dates de `from` à `to`, bornes comprises. */
export function eachDay(from: ISODate, to: ISODate): ISODate[] {
  const days: ISODate[] = [];
  const total = daysBetween(from, to);
  for (let offset = 0; offset <= total; offset += 1) {
    days.push(addDays(from, offset));
  }
  return days;
}
